import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  Signal,
  computed
} from "@angular/core";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import {
  PageFormIdEnum,
  businessProposalInitStep
} from "src/app/classes/enums";
import { BusinessProposalService } from "src/app/services/business-proposal.service";
import { FeatureAppService } from "src/app/services/feature-app.service";
import { FormService } from "src/app/services/form.service";
import { IconService } from "src/app/services/icon.service";
import { MappingService } from "src/app/services/mapping.service";
import { PagingService } from "src/app/services/paging.service";
import { FooterPopupService } from "src/app/services/footer-popup.service";
import { IsNotFeatureAppDirective } from "../../../directives/is-feature-app.directive";
import { FooterMenuComponent } from "../footer-menu/footer-menu.component";

import { IconComponent } from "../icon/icon.component";
import { Router } from "@angular/router";
import { ButtonComponent } from "../../inputs/button/button.component";
import { PricePipe } from "../../../pipes/sharedpipes/price.pipe";
import { FloorPipe } from "../../../pipes/sharedpipes/floor.pipe";
import { DiscountPricePipe } from "../../../pipes/discount-price.pipe";
import { CarBrandPipe } from "../../../pipes/car-brand.pipe";
import { TotalPricePipe } from "../../../pipes/total-price.pipe";
import { UrlService } from "src/app/services/url.service";
import {
  BookingState,
  selectAreAllRequiredPackagesSelected,
  selectBookingState,
  selectBasePackages,
  selectAdditionalPackages,
  selectSelectedBasePackagesForRequest,
  selectSelectedAdditionalPackagesForRequest,
  selectAllSelectedPackagesForRequest,
  selectSelectedEbuCar
} from "src/app/state";
import { Store } from "@ngrx/store";
import {
  EbuCarModelResponse,
  PackageResponse,
  SelectedPackagesRequest,
  WorkshopSystemEnum
} from "src/domain/client";
import { PackagePricePipe } from "src/app/pipes/package-price.pipe";

@Component({
  selector: "app-footer",
  templateUrl: "./footer.component.html",
  imports: [
    IsNotFeatureAppDirective,
    FooterMenuComponent,
    IconComponent,
    ButtonComponent,
    PricePipe,
    FloorPipe,
    TranslateModule,
    DiscountPricePipe,
    CarBrandPipe,
    TotalPricePipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FooterComponent implements OnInit, OnDestroy {
  public PageFormIdEnum = PageFormIdEnum;
  public collapsed = true;
  readonly basePackages: Signal<PackageResponse[] | null>;
  readonly additionalPackages: Signal<PackageResponse[] | null>;
  readonly areAllPackagesSelected: Signal<boolean>;
  readonly booking: Signal<BookingState>;
  readonly selectedBasePackagesForRequest: Signal<
    SelectedPackagesRequest[] | null
  >;
  readonly selectedAdditionalPackagesForRequest: Signal<
    SelectedPackagesRequest[] | null
  >;
  readonly selectedAllPackagesForRequest: Signal<
    SelectedPackagesRequest[] | null
  >;
  readonly selectedEbuCar: Signal<EbuCarModelResponse | null>;

  constructor(
    public formService: FormService,
    public pagingService: PagingService,
    public translateService: TranslateService,
    public mappingService: MappingService,
    private footerPopupService: FooterPopupService,
    public iconService: IconService,
    public featureAppService: FeatureAppService,
    private businessProposalService: BusinessProposalService,
    private carBrandPipe: CarBrandPipe,
    private urlService: UrlService,
    private router: Router,
    private store: Store
  ) {
    this.booking = this.store.selectSignal(selectBookingState);
    this.areAllPackagesSelected = this.store.selectSignal(
      selectAreAllRequiredPackagesSelected
    );
    this.basePackages = this.store.selectSignal(selectBasePackages);
    this.additionalPackages = this.store.selectSignal(selectAdditionalPackages);
    this.selectedBasePackagesForRequest = this.store.selectSignal(
      selectSelectedBasePackagesForRequest
    );
    this.selectedAdditionalPackagesForRequest = this.store.selectSignal(
      selectSelectedAdditionalPackagesForRequest
    );
    this.selectedAllPackagesForRequest = this.store.selectSignal(
      selectAllSelectedPackagesForRequest
    );
    this.selectedEbuCar = this.store.selectSignal(selectSelectedEbuCar);
  }

  readonly isSummaryOpen = computed(
    () => this.footerPopupService.currentView$()?.[0] === "summary"
  );

  private subscription = null;

  ngOnInit(): void {}

  ngOnDestroy(): void {}

  onNavigation(): void {
    if ("parentIFrame" in window) {
      (window as any).parentIFrame.scrollToOffset(0, 0);
    }
    document.getElementById("vbo-header").scrollIntoView(true);
  }

  onBackStep() {
    if (!this.isBackBtnInvalid()) {
      this.onNavigation();
      this.router.navigate([this.pagingService.previousStepUrl], {
        queryParamsHandling: "merge"
      });
    }
  }

  onFowardStep() {
    if (!this.isForwardBtnValid() || this.booking().isPartialLoading) return;

    this.onNavigation();
    this.router.navigate([this.pagingService.nextStepUrl], {
      queryParamsHandling: "merge"
    });
  }

  toggleOpen(): void {
    if (!this.isSummaryOpen()) this.footerPopupService.open("summary");
    else this.footerPopupService.close();
  }

  nextButtonTooltip(): string {
    if (
      this.pagingService.formStepLocation ===
        PageFormIdEnum.chooseSpecifications &&
      !!this.formService.basePackagesResponse &&
      !!this.formService.externalTyreUrlResponse?.externalTyreUrl &&
      !this.formService.chooseSpecificationsForm.valid
    ) {
      return this.translateService.instant("external-tyre-tooltip");
    }
    if (
      this.pagingService.formStepLocation ===
        PageFormIdEnum.chooseSpecifications &&
      !this.areAllPackagesSelected()
    ) {
      if (
        this.booking().selectedWorkshopSystems?.length === 1 &&
        this.booking().selectedWorkshopSystems[0] === WorkshopSystemEnum.Other
      ) {
        return this.translateService.instant(
          "choose-specifications.other.missing-selection-tooltip"
        );
      } else {
        return this.translateService.instant("missing-selection-tooltip");
      }
    }
    return "";
  }

  isBackBtnInvalid(): boolean {
    const isBusinessProposalActiveAndStepLessThanInit =
      this.businessProposalService.isBusinessProposalActive &&
      this.pagingService.formStepLocation <= businessProposalInitStep;

    if (
      !this.formService.isCurrentFormInitialized ||
      isBusinessProposalActiveAndStepLessThanInit
    ) {
      return true;
    }

    return false;
  }

  isForwardBtnValid(): boolean {
    const brandMatch =
      this.urlService.brand ===
        this.carBrandPipe.transform(this.formService.carInfoResponse) ||
      this.urlService.brand === "default";

    if (this.featureAppService.isFeatureApp) {
      return (
        !!this.formService.isCurrentFormValid &&
        !!this.formService.isCurrentFormInitialized &&
        brandMatch
      );
    }
    return (
      !!this.formService.isCurrentFormValid &&
      !!this.formService.isCurrentFormInitialized
    );
  }

  hasAsteriskPrice(): boolean {
    return PackagePricePipe.hasAsteriskPrice(
      this.selectedAllPackagesForRequest(),
      this.basePackages(),
      this.additionalPackages()
    );
  }
}
